import React, { useEffect, useState } from 'react';
import sparkles from '../../Images/konjo-ai-sparkles.svg';
import {
  adminDeleteKonjoMatch,
  adminGetUsers,
  adminListExpiredKonjoMatches,
  adminListKonjoMatches,
  adminMarkKonjoMatchPaid,
  adminRevealKonjoMatchNumber,
  adminSearchKonjoMatches,
  callableErrorMessage,
  confirmAction
} from '../../../adminApi';

function formatWhen(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) {
    return '';
  }
  const ms = n < 1e12 ? n * 1000 : n;
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleString();
}

function displayName(name) {
  const text = name ? String(name).trim() : '';
  return text || 'Unknown';
}

function pairTitle(match) {
  return `${displayName(match.senderName)} and ${displayName(match.receiverName)}`;
}

function speakerLabel(message, match) {
  if (!message.from || message.from === 'bot') {
    return 'Konjo AI';
  }
  if (message.from === match.senderId) {
    return displayName(match.senderName);
  }
  if (message.from === match.receiverId) {
    return displayName(match.receiverName);
  }
  return 'Unknown';
}

function speakerFace(message, match) {
  if (!message.from || message.from === 'bot') {
    return { spark: true, name: 'Konjo AI' };
  }
  if (message.from === match.senderId) {
    return { url: match.senderPhoto, name: match.senderName };
  }
  if (message.from === match.receiverId) {
    return { url: match.receiverPhoto, name: match.receiverName };
  }
  return { name: 'Unknown' };
}

function readerName(userId, match) {
  if (userId === match.senderId) {
    return displayName(match.senderName);
  }
  if (userId === match.receiverId) {
    return displayName(match.receiverName);
  }
  return 'Unknown';
}

function lineBlocks(message, match) {
  const lines = (message.lines || []).filter((line) => line && line.text);
  const unique = [];
  lines.forEach((line) => {
    if (!unique.some((item) => item.text === line.text)) {
      unique.push(line);
    }
  });
  if (unique.length === 0) {
    return [];
  }
  if (unique.length === 1) {
    return [unique[0].text];
  }
  return unique.map((line) => `${readerName(line.userId, match)}: ${line.text}`);
}

function Face(props) {
  const name = displayName(props.name);
  if (props.url) {
    return <img className="admin-match-photo" src={props.url} alt={name} />;
  }
  return <span className="admin-match-photo admin-match-photo--empty">{name.slice(0, 1)}</span>;
}

function Faces(props) {
  const match = props.match;
  return (
    <div className="admin-match-faces">
      <Face url={match.senderPhoto} name={match.senderName} />
      {match.kind === 'konjoAi' ? <img className="admin-match-spark" src={sparkles} alt="Konjo AI" /> : null}
      <Face url={match.receiverPhoto} name={match.receiverName} />
    </div>
  );
}

function NumberButton(props) {
  const [shown, setShown] = useState(false);
  const [number, setNumber] = useState('');
  const [busy, setBusy] = useState(false);
  const name = displayName(props.name);

  const reveal = () => {
    if (shown || busy) {
      return;
    }
    setBusy(true);
    adminRevealKonjoMatchNumber(props.matchId, props.userId, props.source)
      .then((result) => {
        setNumber((result && result.number) || '');
        setShown(true);
      })
      .catch((err) => props.onError(callableErrorMessage(err)))
      .finally(() => setBusy(false));
  };

  if (shown) {
    return <p className="admin-number">{number || 'No number'}</p>;
  }

  return (
    <button type="button" className="admin-btn" onClick={reveal} disabled={busy}>
      {`Show ${name}'s number`}
    </button>
  );
}

function MatchesPanel(props) {
  const [view, setView] = useState('active');
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [matches, setMatches] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const selected = matches.find((match) => match.matchId === selectedId);
  const listExpired = view === 'expired';
  const expired = !searching && listExpired;

  const remember = (loaded, side) => {
    const next = (loaded || []).map((match) => Object.assign({}, match, { side: match.side || side }));
    setMatches(next);
    setSelectedId((current) => {
      if (current && next.some((match) => match.matchId === current)) {
        return current;
      }
      return next[0] ? next[0].matchId : '';
    });
    if (side === 'active' && props.onCount) {
      props.onCount(next.length);
    }
  };

  const load = () => {
    setBusy(true);
    setError('');
    setSearching(false);
    const side = view === 'expired' ? 'expired' : 'active';
    const request = side === 'expired' ? adminListExpiredKonjoMatches() : adminListKonjoMatches();
    return request
      .then((loaded) => remember(loaded, side))
      .catch((err) => setError(callableErrorMessage(err)))
      .finally(() => setBusy(false));
  };

  const runSearch = (value) => {
    setBusy(true);
    setError('');
    setSearching(true);
    return adminSearchKonjoMatches(value)
      .then((loaded) => remember(loaded, ''))
      .catch((err) => setError(callableErrorMessage(err)))
      .finally(() => setBusy(false));
  };

  useEffect(() => {
    load();
  }, [view]);

  const search = (event) => {
    if (event) {
      event.preventDefault();
    }
    const value = query.trim();
    if (!value) {
      load();
      return;
    }
    runSearch(value);
  };

  const showList = (nextView) => {
    setQuery('');
    if (view === nextView) {
      setSearching(false);
      setView(nextView);
      load();
      return;
    }
    setView(nextView);
  };

  const remove = (match) => {
    if (!match || !confirmAction('Expire this match?')) {
      return;
    }
    setBusy(true);
    setError('');
    adminDeleteKonjoMatch(match.matchId)
      .then(() => {
        setMatches((current) => current.filter((item) => item.matchId !== match.matchId));
        setSelectedId((current) => (current === match.matchId ? '' : current));
        if (props.onChanged) {
          props.onChanged();
        }
      })
      .catch((err) => setError(callableErrorMessage(err)))
      .finally(() => setBusy(false));
  };

  const markAlreadyPaid = (match) => {
    if (!match || match.paid) {
      return;
    }
    if (!confirmAction('Mark this match as already paid?')) {
      return;
    }
    setBusy(true);
    setError('');
    adminMarkKonjoMatchPaid(match.matchId)
      .then((result) => {
        if (result && result.alreadyPaid) {
          setMatches((current) => current.map((item) => (
            item.matchId === match.matchId ? Object.assign({}, item, { paid: true }) : item
          )));
          return null;
        }
        if (searching && query.trim()) {
          return runSearch(query.trim());
        }
        return load();
      })
      .catch((err) => setError(callableErrorMessage(err)))
      .finally(() => setBusy(false));
  };

  const openProfile = (userId) => {
    setBusy(true);
    setError('');
    adminGetUsers([userId])
      .then((loaded) => {
        const user = (loaded || [])[0];
        if (user && props.onPreview) {
          props.onPreview(user);
          return;
        }
        setError('No profile.');
      })
      .catch((err) => setError(callableErrorMessage(err)))
      .finally(() => setBusy(false));
  };

  const selectedExpired = selected ? selected.side === 'expired' : expired;

  return (
    <section>
      <div className="admin-section-head">
        <div>
          <h2>Konjo AI Matches</h2>
          <p className="admin-muted">{listExpired ? 'Expired matches.' : 'Active matches.'}</p>
        </div>
        <button
          type="button"
          className="admin-btn"
          onClick={() => {
            const value = query.trim();
            if (searching && value) {
              runSearch(value);
              return;
            }
            load();
          }}
          disabled={busy}
        >
          Refresh
        </button>
      </div>
      <form className="admin-lookup" onSubmit={search}>
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Name, email, or number"
          aria-label="Search matches"
        />
        <button type="submit" className="admin-btn admin-btn--primary" disabled={busy}>Search</button>
      </form>
      <div className="admin-tabs admin-tabs--section" aria-label="Match lists">
        <button type="button" className={!searching && view === 'active' ? 'admin-tab is-active' : 'admin-tab'} onClick={() => showList('active')}>
          Active
        </button>
        <button type="button" className={!searching && view === 'expired' ? 'admin-tab is-active' : 'admin-tab'} onClick={() => showList('expired')}>
          Expired matches
        </button>
      </div>
      {error ? <p className="admin-error">{error}</p> : null}
      {matches.length === 0 && !busy ? (
        <p className="admin-empty">{searching ? 'No matches.' : expired ? 'No expired matches.' : 'No matches.'}</p>
      ) : (
        <div className="admin-split admin-split--scroll">
          <div className="admin-list">
            {matches.map((match) => (
              <button
                type="button"
                key={`${match.side || 'live'}-${match.matchId}`}
                className={match.matchId === selectedId ? 'admin-list-item is-active' : 'admin-list-item'}
                onClick={() => setSelectedId(match.matchId)}
              >
                {searching ? (
                  <span className={match.side === 'expired' ? 'admin-pill admin-pill--old' : 'admin-pill'}>
                    {match.side === 'expired' ? 'Expired' : 'Active'}
                  </span>
                ) : null}
                <span className={match.kind === 'konjoAi' ? 'admin-pill' : 'admin-pill admin-pill--old'}>
                  {match.kind === 'konjoAi' ? 'Konjo AI' : 'Old match'}
                </span>
                <span className={match.paid ? 'admin-pill' : 'admin-pill admin-pill--old'}>
                  {match.paid ? 'Paid' : 'Unpaid'}
                </span>
                <Faces match={match} />
                <strong>{pairTitle(match)}</strong>
                <p className="admin-muted">
                  {match.kind === 'konjoAi'
                    ? [match.step && `Step ${match.step}`, match.place, match.activity].filter(Boolean).join(' · ') || 'Konjo AI'
                    : 'Older production match'}
                </p>
              </button>
            ))}
          </div>
          {selected ? (
            <section className="admin-detail admin-detail--match" key={`${selected.side || 'live'}-${selected.matchId}`}>
              <div className="admin-detail__head">
                {searching ? (
                  <span className={selected.side === 'expired' ? 'admin-pill admin-pill--old' : 'admin-pill'}>
                    {selected.side === 'expired' ? 'Expired' : 'Active'}
                  </span>
                ) : null}
                <span className={selected.kind === 'konjoAi' ? 'admin-pill' : 'admin-pill admin-pill--old'}>
                  {selected.kind === 'konjoAi' ? 'Konjo AI' : 'Old match'}
                </span>
                <span className={selected.paid ? 'admin-pill' : 'admin-pill admin-pill--old'}>
                  {selected.paid ? 'Paid' : 'Unpaid'}
                </span>
                <Faces match={selected} />
                <strong>{pairTitle(selected)}</strong>
                {selected.referenceId ? <p className="admin-muted">{selected.referenceId}</p> : null}
                {selected.kind === 'konjoAi' ? (
                  <p className="admin-muted">
                    {[selected.step && `Step ${selected.step}`, selected.place, selected.activity].filter(Boolean).join(' · ') || 'Konjo AI'}
                  </p>
                ) : (
                  <p className="admin-muted">This is an older match. There is no Konjo AI conversation.</p>
                )}
                {formatWhen(selected.plannedDate) ? (
                  <p className="admin-muted">Planned {formatWhen(selected.plannedDate)}</p>
                ) : null}
              </div>
              {selected.kind === 'konjoAi' ? (
                <div className="admin-thread">
                  {(selected.messages || []).length === 0 ? (
                    <p className="admin-muted">No messages yet.</p>
                  ) : (
                    selected.messages.map((message) => {
                      const face = speakerFace(message, selected);
                      return (
                        <article key={message.id || `${message.from}-${message.created}`} className="admin-bubble">
                          <div className="admin-speaker">
                            {face.spark ? (
                              <img className="admin-match-spark" src={sparkles} alt="" />
                            ) : (
                              <Face url={face.url} name={face.name} />
                            )}
                            <div>
                              <strong>{speakerLabel(message, selected)}</strong>
                              {formatWhen(message.created) ? <p className="admin-muted">{formatWhen(message.created)}</p> : null}
                            </div>
                          </div>
                          {lineBlocks(message, selected).map((line) => (
                            <p key={line}>{line}</p>
                          ))}
                        </article>
                      );
                    })
                  )}
                </div>
              ) : (
                <div className="admin-thread" />
              )}
              <div className="admin-actions admin-actions--pin">
                <button type="button" className="admin-btn" disabled={busy} onClick={() => openProfile(selected.senderId)}>
                  {`${displayName(selected.senderName)}'s profile`}
                </button>
                <button type="button" className="admin-btn" disabled={busy} onClick={() => openProfile(selected.receiverId)}>
                  {`${displayName(selected.receiverName)}'s profile`}
                </button>
                <NumberButton
                  matchId={selected.matchId}
                  userId={selected.senderId}
                  name={selected.senderName}
                  source={selectedExpired ? 'expired' : 'live'}
                  onError={setError}
                />
                <NumberButton
                  matchId={selected.matchId}
                  userId={selected.receiverId}
                  name={selected.receiverName}
                  source={selectedExpired ? 'expired' : 'live'}
                  onError={setError}
                />
                {selectedExpired ? null : (
                  <button
                    type="button"
                    className="admin-btn admin-btn--danger"
                    disabled={busy}
                    onClick={() => remove(selected)}
                  >
                    Expire match
                  </button>
                )}
                {selectedExpired ? null : (
                  <button
                    type="button"
                    className="admin-btn"
                    disabled={busy || selected.paid}
                    onClick={() => markAlreadyPaid(selected)}
                  >
                    Already paid
                  </button>
                )}
              </div>
            </section>
          ) : (
            <p className="admin-empty">Pick a match.</p>
          )}
        </div>
      )}
    </section>
  );
}

export default MatchesPanel;
